import { useCallback, useRef } from 'react';
import type React from 'react';

import { Button, Text } from '@change/ds-core-ui';

import { Translate } from '@change-corgi/core/react/i18n';

import { useStarterDashboardV2PageData } from 'src/app/pages/starterDashboardV2/pageContext';
import { useMarkModuleAsLoaded } from 'src/app/pages/starterDashboardV2/shared/contexts';
import { Waypoint } from 'src/app/shared/components/scroll';

import { CardCarousel } from './components/CardCarousel';
import { QRPosterCard } from './components/QRPoster/components/QRPosterCard';
import { ReadyToSharePostsCard } from './components/ReadyToSharePostsCard';
import { RecommendedGroupsCard } from './components/RecommendedGroupsCard';
import { SocialContentCard } from './components/SocialContent/components/SocialContentCard';
import { useSharingToolsTracking } from './hooks';
import { useSharingTools } from './hooks/useSharingTools';
import { useSharingToolsOrder } from './hooks/useSharingToolsOrder';

export const SharingTools: React.FC<{ viewContext: 'home' }> = ({ viewContext: _viewContext }) => {
	const { data, actions } = useSharingTools();
	const {
		data: {
			featureConfigs: { starterExperienceDashboardPhase4 },
		},
	} = useStarterDashboardV2PageData();
	const { trackView } = useSharingToolsTracking('petition_dashboard_grow_petition_v2');

	useMarkModuleAsLoaded('SharingTools', !!starterExperienceDashboardPhase4?.enabled);

	const hasRecommendedGroups = data.recommendedGroups.isLoading || data.recommendedGroups.groups.length > 0;
	const { orderedKeys, initialIndex, registerClick } = useSharingToolsOrder(hasRecommendedGroups);

	const components: Record<string, JSX.Element> = {
		'recommended-groups': <RecommendedGroupsCard />,
		'ready-to-share-posts': <ReadyToSharePostsCard />,
		'social-content': <SocialContentCard />,
		'qr-poster': <QRPosterCard />,
	};

	const items = orderedKeys.map((key) => ({
		key,
		component: components[key],
	}));

	const isInitialMount = useRef(true);
	const handleActiveIndexChange = useCallback(
		(activeIndex: number) => {
			if (isInitialMount.current) {
				isInitialMount.current = false;
				return;
			}

			if (activeIndex >= 0 && activeIndex < orderedKeys.length) {
				registerClick(orderedKeys[activeIndex]);
			}
		},
		[orderedKeys, registerClick],
	);

	if (!starterExperienceDashboardPhase4?.enabled) {
		return null;
	}

	return (
		<>
			<Waypoint onPositionChange={({ currentPosition }) => trackView(currentPosition)} />
			<div className="flex w-full flex-col gap-5 overflow-x-visible py-8 md:px-12 md:pt-8 md:pb-12">
				<div className="flex flex-row items-center justify-between px-4 md:px-0">
					<Text variant="heading" size="small">
						<Translate value="corgi.petition.starter_dashboard_v2.sharing_tools.title" />
					</Text>
					<Button
						className="px-0 md:px-4"
						data-qa="all-tools-button"
						variant="tertiary"
						size="small"
						onClick={actions.navigateToAllTools}
					>
						<Translate value="corgi.petition.starter_dashboard_v2.sharing_tools.all_tools" />
					</Button>
				</div>
				<CardCarousel
					items={items}
					trackingContext="petition_dashboard_grow_petition_v2"
					initialIndex={initialIndex}
					onActiveIndexChange={handleActiveIndexChange}
				/>
			</div>
		</>
	);
};
